import type { VmsApi } from '@/types/api';
import type { HeatmapRecord } from '@/types/heatmap';
import type { NormalizedRect } from '@/types/player';

import { MOTION_CATEGORY } from './motionFrames';
import { getMotionBoundingBox } from './motionRegion';

/** The server's cap on one page of heatmap rows. */
export const HEATMAP_ROWS_PER_REQUEST = 1000;
/** Requests one card may send for its box; past it, each slice's newest rows stand for it. */
export const MOTION_BOX_MAX_SLICES = 8;
// The motion agent writes about one heatmap row a second.
const MS_PER_ROW = 1000;

interface TimeSlice {
    startMs: number;
    endMs: number;
}

/**
 * The windows to ask for a motion period's rows in. The server answers a window's newest rows
 * first, so one request over a long period would see only its end; equal slices spread what
 * is seen over the whole period.
 */
export function getMotionBoxSlices(startMs: number, endMs: number): TimeSlice[] {
    const spanMs = Math.max(0, endMs - startMs);
    const count = Math.min(
        MOTION_BOX_MAX_SLICES,
        Math.max(1, Math.ceil(spanMs / (HEATMAP_ROWS_PER_REQUEST * MS_PER_ROW))),
    );
    const sliceMs = spanMs / count;
    return Array.from({ length: count }, (_unusedValue, index): TimeSlice => ({
        startMs: index === 0 ? startMs : Math.floor(startMs + index * sliceMs),
        endMs: index === count - 1 ? endMs : Math.floor(startMs + (index + 1) * sliceMs),
    }));
}

/** The box of all motion over a period, from every slice; it fails when any slice fails. */
export async function fetchMotionBox(
    api: Pick<VmsApi, 'getHeatmapRecords'>, cameraId: string, startMs: number, endMs: number,
): Promise<NormalizedRect | null> {
    const pages = await Promise.all(getMotionBoxSlices(startMs, endMs).map(
        (slice): Promise<HeatmapRecord[]> => api.getHeatmapRecords(cameraId, {
            category: MOTION_CATEGORY,
            utcTimeMs: slice.startMs,
            utcEndTimeMs: slice.endMs,
            limit: HEATMAP_ROWS_PER_REQUEST,
        }),
    ));
    return getMotionBoundingBox(pages.flat());
}
